import {
  Button,
  ButtonColor,
  ButtonWidth,
  Modal,
  ModalContent,
  type ModalHook,
  ModalSize,
  ProfileImage,
  useControlledModalState,
} from '@leland-dev/leland-ui-library';
import React, { type FC } from 'react';

import { type Scalars } from '../../../../__generated-gql-types__/globalTypes';
import LoadingIndicator from '../../../common/LoadingIndicator';

import { IntroMessageForm } from './IntroMessageForm';
import { SimpleMessageForm } from './SimpleMessageForm';
import {
  type MessageModalCoachFragment,
  useMessageModalCoachContactsRemainingQuery,
} from './__generated-gql-types__/index.generated';

interface MessageModalHookProps {
  coach: MessageModalCoachFragment;
  categoryUrn: Nullable<Scalars['CategoryUrn']['output']>;
  defaultMessage?: string;
  onSuccess?: () => void;
  onScheduleIntroCall?: () => void;
}

interface MessageModalProps extends MessageModalHookProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

const MessageModalContent: FC<MessageModalProps> = ({
  open,
  setOpen,
  coach,
  categoryUrn,
  defaultMessage = `Hi ${coach.user.firstName}! I'm interested in purchasing coaching from you and would like to talk through my goals together. What does your availability look like in the next week?`,
  onSuccess,
  onScheduleIntroCall,
}) => {
  const {
    data: coachContactsRemaining,
    loading: coachContactsRemainingLoading,
    error: coachContactsRemainingError,
  } = useMessageModalCoachContactsRemainingQuery();

  const hasRemainingCoachContacts =
    !coachContactsRemainingError &&
    !!coachContactsRemaining?.user?.applicant?.introSummary?.hasIntroQuota;

  return (
    <Modal open={open} onOpenChange={setOpen}>
      <ModalContent
        header={
          <div className="flex items-center justify-center space-x-2">
            <ProfileImage className="size-6" {...coach.user} />
            <p>Message {coach.user.firstName}</p>
          </div>
        }
        footer={
          <div className="p-2">
            {coachContactsRemainingLoading ? null : coach.coachingRelationship
                ?.id ? (
              <SendMessageButton formId="simple-message-form" />
            ) : hasRemainingCoachContacts ? (
              <SendMessageButton formId="intro-message-form" />
            ) : (
              <Button
                label={onScheduleIntroCall ? 'Schedule Session' : 'Back'}
                onClick={() => {
                  onScheduleIntroCall?.();
                  setOpen(false);
                }}
                width={ButtonWidth.FULL}
                rounded
              />
            )}
          </div>
        }
        size={ModalSize.SMALL}
        preventScroll
      >
        <div className="p-6">
          {coachContactsRemainingLoading ? (
            <LoadingIndicator iconClassName="w-20 h-20" />
          ) : coach.coachingRelationship?.id ? (
            <SimpleMessageForm
              coach={coach}
              defaultMessage={defaultMessage}
              onSuccess={onSuccess}
              onRequestClose={() => setOpen(false)}
            />
          ) : hasRemainingCoachContacts ? (
            <IntroMessageForm
              coach={coach}
              categoryUrn={categoryUrn}
              defaultMessage={defaultMessage}
              onSuccess={onSuccess}
              onScheduleIntroCall={onScheduleIntroCall}
              onRequestClose={() => setOpen(false)}
            />
          ) : (
            <div className="w-full rounded-lg bg-leland-gray-hover p-4">
              <p className="text-sm text-leland-gray-light">
                You&rsquo;ve reached your limit for the amount of new
                conversations you can start today. To connect with{' '}
                {coach.user.firstName}, you can{' '}
                {onScheduleIntroCall ? (
                  <button
                    className="underline underline-offset-2"
                    onClick={() => {
                      onScheduleIntroCall();
                      setOpen(false);
                    }}
                  >
                    book a coaching session.
                  </button>
                ) : (
                  'book a coaching session.'
                )}
              </p>
            </div>
          )}
        </div>
      </ModalContent>
    </Modal>
  );
};

interface SendMessageButtonProps {
  formId: string;
}

const SendMessageButton: FC<SendMessageButtonProps> = ({ formId }) => {
  return (
    <Button
      form={formId}
      type="submit"
      label="Send Message"
      buttonColor={ButtonColor.PRIMARY}
      width={ButtonWidth.FULL}
      rounded
    />
  );
};

export const useMessageModal: ModalHook<MessageModalHookProps> = () => {
  const [open, setOpen] = useControlledModalState();
  const MessageModal: FC<MessageModalHookProps> = (props) => {
    return open ? (
      <MessageModalContent {...props} open={open} setOpen={setOpen} />
    ) : null;
  };
  return { open, setOpen, Modal: MessageModal };
};
